<script setup>
import { computed } from "vue";
import { StealieLogo, StealieText, StealieLink } from "@get-overalls/fe-stealie";
import InitialsAvatar from "@/components/InitialsAvatar.vue";
import getInitials from "@/utilities/getInitials";
import { getRelativeTime } from "@/utilities/getRelativeTime";
import { PaperClipIcon } from "@heroicons/vue/24/outline";

const props = defineProps({
  author: {
    type: String,
    required: true,
  },
  source: {
    type: String,
    required: true,
  },
  message: {
    type: String,
    required: true,
  },
  timestamp: {
    type: String,
    required: true,
  },
  attachments: {
    type: Array,
    required: false,
    default: () => [],
  },
});

const hasAttachments = computed(() => props.attachments.length > 0);
const fileNameFromKey = (key) => key.split("/").slice(2).join("/");
</script>

<template>
  <div class="mx-auto flex w-full gap-x-3 md:max-w-3xl" data-sr-mask>
    <div class="shrink-0">
      <InitialsAvatar
        v-if="source === 'user'"
        :initials="getInitials(author)"
        class="size-8 bg-brand-secondary-raspberry-400 sm:size-12"
      />
      <StealieLogo v-else variant="logo" class="size-8 sm:size-12" />
    </div>
    <div
      :class="[
        'relative p-4 pt-10 sm:p-8 sm:pt-12',
        'flex-1 rounded border border-brand-gray-50',
        'bg-white',
        'overflow-hidden',
      ]"
    >
      <!-- eslint-disable-next-line vue/no-v-html -->
      <div class="rich-message-content" v-html="message"></div>
      <StealieText class="absolute top-3.5 left-4 text-xs sm:left-8">
        {{ getRelativeTime(timestamp) }}
      </StealieText>
      <div v-if="hasAttachments" class="mt-8 flex flex-col gap-y-1">
        <StealieText class="flex items-center gap-x-1" size="sm" bold>
          <PaperClipIcon class="size-4" />
          Attachment{{ attachments.length > 1 ? "s" : "" }}
        </StealieText>
        <ul>
          <li v-for="fileKey in attachments" :key="fileKey">
            <StealieLink
              :to="`/attachments/${encodeURIComponent(fileKey)}`"
              target="_blank"
            >
              <StealieText size="sm"> {{ fileNameFromKey(fileKey) }}</StealieText>
            </StealieLink>
          </li>
        </ul>
      </div>
    </div>
  </div>
</template>

<style scoped>
@reference "@/tailwind.css";

:deep(.rich-message-content) {
  :last-child {
    @apply mb-0;
  }

  p,
  ul,
  ol,
  table,
  pre,
  hr {
    @apply mb-4;
  }

  ul > li {
    @apply ml-4 list-disc;
  }

  ol > li {
    @apply ml-4 list-decimal;
  }

  a {
    @apply cursor-pointer font-bold text-brand-primary-teal-400 underline visited:text-brand-primary-teal-500 hover:text-brand-primary-teal-300 active:text-brand-primary-teal-500;
  }

  strong {
    @apply font-bold;
  }

  em {
    @apply italic;
  }

  h1,
  h2,
  h3,
  h4,
  h5,
  h6 {
    @apply mb-2 font-bold;
  }

  h1 {
    @apply text-2xl;
  }

  h2 {
    @apply text-xl;
  }

  h3 {
    @apply text-lg;
  }

  table {
    @apply inline-block max-w-full overflow-x-auto rounded-sm border border-brand-gray-200;
  }

  th {
    @apply bg-brand-gray-25 font-semibold;
  }

  td,
  th {
    @apply border border-brand-gray-200 p-2;
  }

  /* Hide the borders on the outer edges of the table, since the table's border is already visible */
  /* Top row */
  th {
    @apply border-t-0;
  }

  /* Left column */
  :is(td, th):first-of-type {
    @apply border-l-0;
  }

  /* Right column */
  :is(td, th):last-of-type {
    @apply border-r-0;
  }

  /* Bottom row */
  tr:last-of-type td {
    @apply border-b-0;
  }

  code {
    @apply rounded-sm border border-brand-gray-100 bg-brand-gray-25 p-1 text-sm;
  }

  pre > code {
    @apply block rounded-sm border border-brand-gray-100 bg-brand-gray-25 p-4;
  }

  hr {
    @apply border-brand-gray-200;
  }
}
</style>
